/**
 * InvoiceList Component
 * Displays list of invoices with filtering, status, and send functionality
 * L0-CMD-2026-0120-007 - Wired to API
 */

import React, { useState, useEffect } from 'react';

interface Invoice {
  id: number;
  invoice_number: string;
  client_id: number;
  client_name?: string;
  client_email?: string;
  project_id: number;
  project_name?: string;
  total: number;
  amount_paid: number;
  status: string;
  due_date: string;
  sent_at?: string;
  paid_at?: string;
  created_at: string;
}

interface InvoiceListProps {
  clientId?: number;
  projectId?: number;
}

const InvoiceList: React.FC<InvoiceListProps> = ({ clientId, projectId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [sendingId, setSendingId] = useState<number | null>(null);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchInvoices();
  }, [filter, clientId, projectId]);

  const fetchInvoices = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      let url = `${API_BASE}/api/invoices`;

      // Add filter query params
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }
      if (clientId) {
        params.append('client_id', clientId.toString());
      }
      if (projectId) {
        params.append('project_id', projectId.toString());
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }

      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const sendInvoice = async (invoiceId: number) => {
    setSendingId(invoiceId);

    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE}/api/invoices/${invoiceId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          include_payment_link: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send invoice');
      }

      const result = await response.json();

      // Show success notification
      alert(result.message || 'Invoice sent successfully!');

      // Refresh list to update status
      fetchInvoices();
    } catch (err) {
      console.error('Failed to send invoice:', err);
      alert(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setSendingId(null);
    }
  };

  const viewInvoice = (invoiceId: number) => {
    window.open(`/invoices/${invoiceId}`, '_blank');
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filter === 'all') return true;
    return invoice.status === filter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'var(--success, #10b981)';
      case 'sent': return 'var(--primary, #2563eb)';
      case 'draft': return 'var(--secondary, #64748b)';
      case 'partial': return 'var(--warning, #f59e0b)';
      case 'overdue': return 'var(--danger, #ef4444)';
      case 'cancelled': return 'var(--muted, #475569)';
      default: return 'var(--secondary, #64748b)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent';
      case 'viewed': return 'Viewed';
      case 'partial': return 'Partial';
      case 'paid': return 'Paid';
      case 'overdue': return 'Overdue';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const totalPending = invoices
    .filter(i => i.status !== 'paid' && i.status !== 'cancelled')
    .reduce((sum, i) => sum + (i.total - (i.amount_paid || 0)), 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="invoice-list">
      <div className="list-header">
        <h2>Invoices</h2>
        <div className="header-stats">
          <span className="pending-amount">
            {formatCurrency(totalPending)} outstanding
          </span>
        </div>
      </div>

      <div className="filters">
        {['all', 'draft', 'sent', 'partial', 'paid', 'overdue'].map(f => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : getStatusLabel(f)}
          </button>
        ))}
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchInvoices} className="retry-btn">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading invoices...
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices found</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Project</th>
              <th>Client</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map(invoice => (
              <tr key={invoice.id}>
                <td className="invoice-number">{invoice.invoice_number}</td>
                <td>{invoice.project_name || '-'}</td>
                <td>{invoice.client_name || '-'}</td>
                <td className="amount">
                  {formatCurrency(invoice.total)}
                  {invoice.amount_paid > 0 && invoice.amount_paid < invoice.total && (
                    <span className="paid-amount">
                      ({formatCurrency(invoice.amount_paid)} paid)
                    </span>
                  )}
                </td>
                <td>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: `${getStatusColor(invoice.status)}20`,
                      color: getStatusColor(invoice.status)
                    }}
                  >
                    {getStatusLabel(invoice.status)}
                  </span>
                </td>
                <td className={invoice.status === 'overdue' ? 'overdue-date' : ''}>
                  {formatDate(invoice.due_date)}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn small"
                      onClick={() => viewInvoice(invoice.id)}
                    >
                      View
                    </button>
                    {invoice.status === 'draft' && (
                      <button
                        className="btn small primary"
                        onClick={() => sendInvoice(invoice.id)}
                        disabled={sendingId === invoice.id}
                      >
                        {sendingId === invoice.id ? 'Sending...' : 'Send'}
                      </button>
                    )}
                    {(invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'partial') && (
                      <button
                        className="btn small secondary"
                        onClick={() => sendInvoice(invoice.id)}
                        disabled={sendingId === invoice.id}
                      >
                        {sendingId === invoice.id ? 'Sending...' : 'Resend'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <style>{`
        .invoice-list {
          padding: 0;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .list-header h2 {
          margin: 0;
          color: var(--text-primary, #f1f5f9);
        }

        .pending-amount {
          font-size: 14px;
          color: var(--warning, #f59e0b);
          font-weight: 500;
        }

        .filters {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid transparent;
          border-radius: 20px;
          color: var(--text-secondary, #94a3b8);
          cursor: pointer;
          transition: all 0.2s;
          font-size: 13px;
        }

        .filter-btn:hover {
          border-color: var(--primary, #2563eb);
          color: var(--text-primary, #f1f5f9);
        }

        .filter-btn.active {
          background: var(--primary, #2563eb);
          color: white;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th,
        .data-table td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--border-color, #334155);
        }

        .data-table th {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--text-secondary, #94a3b8);
          background: var(--bg-secondary, #1e293b);
        }

        .data-table td {
          color: var(--text-primary, #f1f5f9);
        }

        .data-table tr:hover {
          background: var(--bg-tertiary, #334155);
        }

        .invoice-number {
          font-family: monospace;
          font-weight: 500;
        }

        .amount {
          font-weight: 600;
        }

        .paid-amount {
          display: block;
          font-size: 11px;
          font-weight: 400;
          color: var(--text-secondary, #94a3b8);
        }

        .overdue-date {
          color: var(--danger, #ef4444);
        }

        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn {
          padding: 6px 12px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 12px;
          font-weight: 500;
        }

        .btn.small {
          padding: 6px 12px;
          font-size: 12px;
        }

        .btn:not(.primary):not(.secondary) {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary, #f1f5f9);
        }

        .btn:not(.primary):not(.secondary):hover {
          background: #475569;
        }

        .btn.primary {
          background: var(--primary, #2563eb);
          color: white;
        }

        .btn.primary:hover:not(:disabled) {
          background: #1d4ed8;
        }

        .btn.secondary {
          background: var(--bg-tertiary, #334155);
          color: var(--text-secondary, #94a3b8);
          border: 1px solid var(--border-color, #475569);
        }

        .btn.secondary:hover:not(:disabled) {
          background: #475569;
          color: var(--text-primary, #f1f5f9);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .loading {
          text-align: center;
          padding: 60px 40px;
          color: var(--text-secondary, #94a3b8);
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--bg-tertiary, #334155);
          border-top-color: var(--primary, #2563eb);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state {
          text-align: center;
          padding: 60px 40px;
          color: var(--text-secondary, #94a3b8);
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid var(--danger, #ef4444);
          color: var(--danger, #ef4444);
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .retry-btn {
          background: var(--danger, #ef4444);
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .retry-btn:hover {
          background: #dc2626;
        }

        @media (max-width: 768px) {
          .data-table {
            display: block;
            overflow-x: auto;
          }

          .filters {
            overflow-x: auto;
            padding-bottom: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceList;
